import React, { useState, useEffect } from 'react';
import { Star, X } from 'lucide-react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';

function BannerContent({ citaId, profesionalId, pacienteId, fechaHoraInicio, onDismiss, onSuccess }: any) {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shouldShow, setShouldShow] = useState<boolean | null>(null);
  const storageKey = `dismissed_review_${citaId}`;
  const [isDismissed, setIsDismissed] = useState(() => localStorage.getItem(storageKey) === 'true');

  useEffect(() => {
    // 1. Check 7 days limit
    const appointmentDate = new Date(fechaHoraInicio);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    if (appointmentDate < sevenDaysAgo) {
      setShouldShow(false);
      return;
    }

    // 2. Check if already reviewed
    const checkReview = async () => {
      const { data } = await supabase
        .from('evaluaciones_profesionales')
        .select('id')
        .eq('cita_id', citaId)
        .eq('paciente_uuid', pacienteId)
        .single();
      
      if (data) {
        setShouldShow(false); // Already reviewed
      } else {
        setShouldShow(true);
      }
    };
    checkReview();
  }, [citaId, pacienteId, fechaHoraInicio]);

  if (shouldShow === false) return null;
  if (shouldShow === null) return null; // loading state
  if (isDismissed) return null;

  const submitReview = async () => {
    if (rating === 0) return;
    setLoading(true);
    setError('');

    const { error: dbError } = await supabase.from('evaluaciones_profesionales').insert({
      cita_id: citaId,
      profesional_uuid: profesionalId,
      paciente_uuid: pacienteId,
      puntuacion: rating,
      comentario: comment || null
    });

    if (dbError) {
      if (dbError.code === '23505') {
        setError('Ya has calificado esta cita.');
      } else {
        setError('Ocurrió un error. Intenta de nuevo.');
      }
      setLoading(false);
    } else {
      setLoading(false);
      onSuccess?.();
    }
  };

  return (
    <div style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', padding: '16px', marginBottom: '16px', position: 'relative' }}>
      <button 
        onClick={() => { 
          setIsDismissed(true); 
          localStorage.setItem(storageKey, 'true');
          onDismiss?.(); 
        }} 
        style={{ position: 'absolute', top: '12px', right: '12px', background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <X size={16} color="#92400E" />
      </button>
      
      <h4 style={{ margin: '0 0 8px 0', color: '#92400E', fontSize: '15px' }}>¿Cómo te fue en tu cita?</h4>
      <p style={{ margin: '0 0 16px 0', color: '#B45309', fontSize: '13px' }}>Ayuda a otros pacientes calificando al profesional.</p>
      
      <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => setRating(star)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <Star size={28} fill={(hovered || rating) >= star ? '#F59E0B' : 'none'} color={(hovered || rating) >= star ? '#F59E0B' : '#D1D5DB'} />
          </button>
        ))}
      </div>

      {rating > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Comentario opcional..."
            style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #FCD34D', fontSize: '14px', resize: 'vertical', minHeight: '60px' }}
          />
          {error && <span style={{ color: '#EF4444', fontSize: '12px', fontWeight: 600 }}>{error}</span>}
          <button
            onClick={submitReview}
            disabled={loading}
            style={{ backgroundColor: '#F59E0B', color: '#FFF', border: 'none', padding: '10px 16px', borderRadius: '8px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Enviando...' : 'Enviar calificación'}
          </button>
        </div>
      )}
    </div>
  );
}

class ReviewErrorBoundary extends React.Component<any, { hasError: boolean }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) return null; // Falla silenciosamente (Zero-Trust UI isolation)
    return this.props.children;
  }
}

export function ReviewReminderBanner(props: any) {
  return (
    <ReviewErrorBoundary>
      <BannerContent {...props} />
    </ReviewErrorBoundary>
  );
}
